import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  serverTimestamp,
  FieldValue
} from 'firebase/firestore';
import { db } from '../firebase';
import EmailService from './emailService';
import { getEmailTemplate } from './emailTemplates';
import { contactService } from './contactService';
import { tagService } from './tagService';
import { prepareAutomationEmailHtml } from '../utils/automationEmailLinkTracking';
import { applyUnsubscribeUrlToHtml, buildUnsubscribeApiUrl } from '../utils/unsubscribeUrl';

// Core Automation Types
export interface AutomationTrigger {
  id: string;
  type: 'contact_added' | 'contact_updated' | 'contact_tag_added' | 'contact_tag_removed' |
  'email_opened' | 'email_clicked' | 'email_bounced' | 'email_unsubscribed' |
  'added_product_to_cart' | 'started_checkout' | 'abandoned_cart' | 'placed_order' | 'viewed_product' |
  'date_trigger' | 'anniversary' | 'birthday' | 'manual' | 'api_trigger';
  config: {
    // Contact triggers
    contactField?: string;
    contactValue?: any;
    tagId?: string;

    // Email triggers
    campaignId?: string;
    emailId?: string;

    // E-commerce triggers
    productId?: string;
    cartValue?: number;
    orderId?: string;
    
    // Date triggers
    triggerDate?: Timestamp;
    triggerTime?: string; // HH:MM format
    timezone?: string;

    // Anniversary triggers
    anniversaryField?: string; // 'birthday', 'signup_date', etc.
    daysBefore?: number;
    daysAfter?: number;

    // Manual/API triggers
    triggerKey?: string;
  };
}

export interface AutomationCondition {
  id: string;
  type: 'contact_field' | 'contact_tag' | 'contact_segment' | 'email_behavior' | 'date_condition' | 'custom';
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'is_empty' | 'is_not_empty';
  value: any;
  config: {
    field?: string;
    tagId?: string;
    segmentId?: string;
    emailId?: string;
    behavior?: 'opened' | 'clicked' | 'bounced' | 'unsubscribed';
    dateField?: string;
    daysAgo?: number;
  };
}

export interface AutomationAction {
  id: string;
  type: 'send_email' | 'send_sms' | 'send_push' | 'add_tag' | 'remove_tag' | 'update_contact' |
  'add_to_segment' | 'remove_from_segment' | 'update_lead_score' | 'wait' | 'delay' |
  'webhook' | 'conditional_split' | 'ab_test' | 'track_goal' | 'track_conversion';
  config: {
    // Email action
    emailName?: string;
    emailTemplateId?: string;
    emailTemplateName?: string;
    subject?: string;
    subjectB?: string; // legacy field support
    preheader?: string;
    fromName?: string;
    fromEmail?: string;
    replyTo?: string;
    utm?: boolean;
    tracking?: {
      open?: boolean;
      click?: boolean;
      utm?: boolean;
    };
    sendOptions?: {
      mode?: 'immediate' | 'delay' | 'specific_time';
      delayValue?: number;
      delayUnit?: 'minutes' | 'hours' | 'days';
      sendTime?: string; // HH:mm
      timezone?: string;
    };
    conditionalSend?: {
      enabled: boolean;
      tagId?: string;
      tagName?: string;
    };
    personalizationTokens?: string[];
    abTest?: {
      enabled: boolean;
      subjectB?: string;
      split?: number; // percentage for variant A (0-100)
    };
    analyticsPreview?: {
      projectedOpens?: number;
      projectedClicks?: number;
      sampleSize?: number;
    };
    // Support for simple inline system emails
    htmlBody?: string;

    // SMS action
    smsMessage?: string;
    smsFrom?: string;

    // Push notification action
    pushTitle?: string;
    pushMessage?: string;
    pushIcon?: string;
    pushUrl?: string;

    // Tag actions
    tagId?: string;
    tagName?: string;

    // Contact update
    fieldUpdates?: { [key: string]: any };

    // Segment actions
    segmentId?: string;

    // Lead score action
    scoreChange?: number; // Can be positive or negative
    scoreReason?: string;

    // Wait/Delay actions
    waitType?: 'fixed' | 'business_days' | 'until_date' | 'until_time_of_day';
    waitValue?: number; // hours for fixed, days for business_days
    waitUntilDate?: Timestamp;
    waitUntilTime?: string; // HH:MM format
    timezone?: string;

    // Webhook action
    webhookUrl?: string;
    webhookMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    webhookHeaders?: { [key: string]: string };
    webhookBody?: any;
    webhookRetries?: number;
    webhookTimeout?: number;

    // Conditional split
    conditions?: AutomationCondition[];
    truePath?: string; // next step ID for true condition
    falsePath?: string; // next step ID for false condition

    // A/B Test action
    abVariants?: {
      name: string;
      percentage: number;
      emailTemplateId?: string;
      subject?: string;
      nextStepId?: string;
    }[];
    abGoal?: 'open_rate' | 'click_rate' | 'conversion_rate' | 'revenue';
    abWinnerDelay?: number; // hours to wait before selecting winner
    abAutoSelectWinner?: boolean;

    // Goal tracking
    goalName?: string;
    goalValue?: number;
    goalMetadata?: { [key: string]: any };

    // Conversion tracking
    conversionName?: string;
    conversionValue?: number;
    conversionMetadata?: { [key: string]: any };
  };
}

export interface AutomationStep {
  id: string;
  type: 'trigger' | 'condition' | 'action' | 'delay' | 'split' | 'exit';
  title: string;
  description: string;
  config: AutomationTrigger | AutomationCondition | AutomationAction;
  position: { x: number; y: number };
  connections: string[]; // IDs of connected steps
  enabled: boolean;
}

export interface AutomationGoal {
  id: string;
  name: string;
  type: 'conversion' | 'revenue' | 'engagement' | 'custom';
  targetValue?: number;
  currentValue: number;
  completionRate: number; // percentage
  trackingMethod: 'email_open' | 'email_click' | 'page_visit' | 'purchase' | 'custom_event';
  metadata?: { [key: string]: any };
}

export interface Automation {
  id?: string;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'archived';
  userId: string;
  category?: 'ecommerce' | 'marketing' | 'platform' | 'system';
  systemKey?: string; // e.g. 'user_verification', 'password_reset'
  workspaceId?: string; // Added for workspace-specific automations
  autoPausedAt?: Timestamp;       // set when the system auto-pauses due to consecutive failures
  autoPauseReason?: string;       // human-readable reason for the auto-pause
  steps: AutomationStep[];
  settings: {
    allowRepetition: boolean;
    cooldownHours?: number;      // minimum hours between re-enrollments (only when allowRepetition: true)
    openTracking: boolean;
    clickTracking: boolean;
    personalizedToField: boolean;
    googleAnalyticsTracking: boolean;
    timezone?: string;
    sendingWindow?: {
      enabled: boolean;
      startTime: string; // HH:MM
      endTime: string; // HH:MM
      daysOfWeek: number[]; // 0-6, Sunday = 0
    };
    throttling?: {
      enabled: boolean;
      maxPerHour?: number;
      maxPerDay?: number;
    };
  };
  statistics: {
    totalRuns: number;
    successfulRuns: number;
    failedRuns: number;
    contactsProcessed: number;
    emailsSent: number;
    smsSent?: number;
    pushSent?: number;
    emailsOpened?: number;
    emailsClicked?: number;
    emailsBounced?: number;
    emailsUnsubscribed?: number;
    conversions?: number;
    revenue?: number;
    averageCompletionTime?: number; // in minutes
    goalCompletions?: { [goalId: string]: number };
    lastRunAt?: Timestamp;
    nextRunAt?: Timestamp;
    [key: string]: any; // Index signature for dynamic property access
  };
  goals?: AutomationGoal[];
  version: number;
  previousVersions?: string[]; // IDs of previous versions for rollback
  tags?: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastTriggeredAt?: Timestamp;
}

export interface AutomationExecution {
  id: string;
  automationId: string;
  contactId: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  currentStepId: string;
  stepHistory: {
    stepId: string;
    executedAt: Timestamp | FieldValue | Date; // Allow Date objects for array compatibility
    status: 'success' | 'failed' | 'skipped';
    result?: any;
    error?: string;
  }[];
  startedAt: Timestamp | FieldValue;
  completedAt?: Timestamp | FieldValue;
  error?: string;
  context: {
    contactData: any;
    triggerData: any;
    variables: { [key: string]: any };
  };
}

export interface AutomationTemplate {
  id: string;
  name: string;
  description: string;
  category: 'welcome' | 'nurture' | 're_engagement' | 'transactional' | 'marketing' | 'custom';
  steps: Omit<AutomationStep, 'id' | 'position'>[];
  estimatedDuration: string; // e.g., "7 days", "2 weeks"
  tags: string[];
  isPublic: boolean;
  createdBy: string;
  createdAt: Timestamp;
}

export const resolveInitialStepId = (automation: Pick<Automation, 'steps'>): string | null => {
  const triggerStep = automation.steps.find((step) => step.type === 'trigger');
  if (triggerStep && Array.isArray(triggerStep.connections) && triggerStep.connections.length > 0) {
    const firstConnected = triggerStep.connections.find((stepId) =>
      automation.steps.some((step) => step.id === stepId)
    );
    if (firstConnected) {
      return firstConnected;
    }
  }

  const firstRunnable = automation.steps.find(
    (step) => step.type !== 'trigger' && step.type !== 'exit'
  );
  return firstRunnable?.id || null;
};

class AutomationService {
  private executions: Map<string, NodeJS.Timeout> = new Map();

  // Automation CRUD Operations
  
  // Helper function to remove undefined values recursively (Firestore doesn't accept them)
  private removeUndefined(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefined(item));
    }
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const key in obj) {
        if (obj[key] !== undefined) {
          cleaned[key] = this.removeUndefined(obj[key]);
        }
      }
      return cleaned;
    }
    return obj;
  }
  
  async createAutomation(automation: Omit<Automation, 'id' | 'createdAt' | 'updatedAt' | 'statistics' | 'version'>): Promise<string> {
    try {
      console.log('🔵 Creating automation with data:', {
        name: automation.name,
        userId: automation.userId,
        workspaceId: automation.workspaceId,
        status: automation.status,
        stepsCount: automation.steps?.length
      });

      const automationData = {
        ...automation,
        version: 1,
        statistics: {
          totalRuns: 0,
          successfulRuns: 0,
          failedRuns: 0,
          contactsProcessed: 0,
          emailsSent: 0,
          smsSent: 0,
          pushSent: 0,
          emailsOpened: 0,
          emailsClicked: 0,
          emailsBounced: 0,
          emailsUnsubscribed: 0,
          conversions: 0,
          revenue: 0,
          averageCompletionTime: 0,
          goalCompletions: {}
        },
        goals: automation.goals || [],
        tags: automation.tags || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Remove undefined values before sending to Firestore
      const cleanedData = this.removeUndefined(automationData);
      
      console.log('🔵 Cleaned data for Firestore:', {
        userId: cleanedData.userId,
        workspaceId: cleanedData.workspaceId,
        name: cleanedData.name,
        hasUserId: !!cleanedData.userId,
        userIdType: typeof cleanedData.userId
      });

      const docRef = await addDoc(collection(db, 'automations'), cleanedData);
      console.log('✅ Automation created successfully with ID:', docRef.id);
      return docRef.id;
    } catch (error: any) {
      console.error('❌ Error creating automation:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Automation data:', {
        name: automation.name,
        userId: automation.userId,
        workspaceId: automation.workspaceId
      });
      
      // Provide more detailed error message
      if (error instanceof Error) {
        throw new Error(`Failed to create automation: ${error.message}`);
      }
      throw new Error('Failed to create automation');
    }
  }

  async getAutomation(automationId: string): Promise<Automation | null> {
    try {
      const docRef = doc(db, 'automations', automationId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Automation;
      }
      return null;
    } catch (error) {
      console.error('Error getting automation:', error);
      throw new Error('Failed to get automation');
    }
  }

  async getAutomations(userId: string, workspaceId?: string): Promise<Automation[]> {
    try {
      console.log('Getting automations for user:', userId, 'workspaceId:', workspaceId);

      if (!userId) {
        console.warn('No userId provided, returning empty array');
        return [];
      }

      // Build query with workspace filter if provided
      let q;
      if (workspaceId) {
        q = query(
          collection(db, 'automations'),
          where('userId', '==', userId),
          where('workspaceId', '==', workspaceId)
        );
      } else {
        q = query(
          collection(db, 'automations'),
          where('userId', '==', userId)
        );
      }

      const querySnapshot = await getDocs(q);
      console.log('Found automations:', querySnapshot.docs.length);

      if (querySnapshot.empty) {
        console.log('No automations found for user, returning empty array');
        return [];
      }

      const automations = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Automation data:', { id: doc.id, name: data.name, updatedAt: data.updatedAt });
        return {
          id: doc.id,
          ...data
        } as Automation;
      });

      // Sort manually since orderBy might fail if updatedAt is missing
      const sortedAutomations = automations.sort((a, b) => {
        const aTime = a.updatedAt?.toDate?.() || new Date(0);
        const bTime = b.updatedAt?.toDate?.() || new Date(0);
        return bTime.getTime() - aTime.getTime();
      });

      console.log('Returning sorted automations:', sortedAutomations.length);
      return sortedAutomations;
    } catch (error) {
      console.error('Error getting automations:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        code: (error as any)?.code,
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error instanceof Error ? error : new Error('Failed to get automations');
    }
  }

  async updateAutomation(automationId: string, updates: Partial<Automation>): Promise<void> {
    try {
      const docRef = doc(db, 'automations', automationId);
      
      // Remove undefined values before sending to Firestore
      const cleanedUpdates = this.removeUndefined({
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // Log the data being saved for debugging
      console.log('Updating automation:', automationId);
      
      await updateDoc(docRef, cleanedUpdates);
    } catch (error) {
      console.error('Error updating automation:', error);
      console.error('Failed automation updates:', updates);
      
      // Provide more detailed error message
      if (error instanceof Error) {
        throw new Error(`Failed to update automation: ${error.message}`);
      }
      throw new Error('Failed to update automation');
    }
  }

  async deleteAutomation(automationId: string): Promise<void> {
    try {
      const docRef = doc(db, 'automations', automationId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting automation:', error);
      throw new Error('Failed to delete automation');
    }
  }

  // Automation Execution
  async triggerAutomation(automationId: string, contactId: string, triggerData: any = {}): Promise<string> {
    try {
      const automation = await this.getAutomation(automationId);
      if (!automation) {
        throw new Error('Automation not found');
      }

      if (automation.status !== 'active') {
        throw new Error('Automation is not active');
      }

      // Get contact data
      const contact = await contactService.getContact(contactId);
      if (!contact) {
        throw new Error('Contact not found');
      }

      // Create execution record
      const initialStepId = this.getFirstStepId(automation);
      if (!initialStepId) {
        throw new Error('Automation has no valid starting step');
      }

      const executionId = await this.createExecution(automationId, contactId, triggerData, contact, initialStepId);

      // The Cloud Function processAutomationExecution (onCreate on automationExecutions)
      // picks this up and runs it server-side. No client-side execution needed.
      console.log(`✅ Execution ${executionId} queued — Cloud Function will process it.`);

      return executionId;
    } catch (error) {
      console.error('Error triggering automation:', error);
      throw error;
    }
  }

  private async createExecution(
    automationId: string,
    contactId: string,
    triggerData: any,
    contactData: any,
    initialStepId: string
  ): Promise<string> {
    // Get the automation to extract userId for security rules
    const automation = await this.getAutomation(automationId);
    if (!automation) {
      throw new Error(`Automation ${automationId} not found when creating execution`);
    }

    const executionData = {
      automationId,
      contactId,
      userId: automation.userId, // ✅ CRITICAL: Required for Firestore security rules
      workspaceId: automation.workspaceId || contactData.workspaceId,
      status: 'pending',
      currentStepId: initialStepId,
      stepHistory: [],
      startedAt: serverTimestamp(),
      context: {
        contactData,
        triggerData,
        variables: {}
      }
    };

    const docRef = await addDoc(collection(db, 'automationExecutions'), executionData);
    console.log(`✅ Created execution ${docRef.id} for automation ${automationId}, userId: ${automation.userId}`);
    return docRef.id;
  }

  private getFirstStepId(automation: Automation): string | null {
    return resolveInitialStepId(automation);
  }

  private async executeAutomation(executionId: string): Promise<void> {
    try {
      const execution = await this.getExecution(executionId);
      if (!execution) {
        throw new Error('Execution not found');
      }

      const automation = await this.getAutomation(execution.automationId);
      if (!automation) {
        throw new Error('Automation not found');
      }

      // Update execution status
      await this.updateExecution(executionId, { status: 'running' });

      // Execute steps
      await this.executeSteps(executionId, automation, execution);

      // Mark as completed
      await this.updateExecution(executionId, {
        status: 'completed',
        completedAt: serverTimestamp()
      });

    } catch (error) {
      console.error('Error executing automation:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      await this.updateExecution(executionId, {
        status: 'failed',
        error: errorMessage,
        completedAt: serverTimestamp()
      });
    }
  }

  private async executeSteps(executionId: string, automation: Automation, execution: AutomationExecution): Promise<void> {
    let currentStepId = execution.currentStepId;

    while (currentStepId) {
      const step = automation.steps.find(s => s.id === currentStepId);
      if (!step) {
        throw new Error(`Execution halted: step "${currentStepId}" was not found in automation`);
      }

      try {
        const result = await this.executeStep(step, execution);

        // Record step execution
        await this.recordStepExecution(executionId, step.id, 'success', result, undefined, automation);

        // Move to next step
        const nextStepId = this.getNextStepId(step, result);
        currentStepId = nextStepId || '';

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        await this.recordStepExecution(executionId, step.id, 'failed', null, errorMessage, automation);
        break;
      }
    }
  }

  private async executeStep(step: AutomationStep, execution: AutomationExecution): Promise<any> {
    switch (step.type) {
      case 'action':
        return await this.executeAction(step.config as AutomationAction, execution);
      case 'condition':
        return await this.evaluateCondition(step.config as AutomationCondition, execution);
      case 'delay':
        return await this.delayAction(step.config as AutomationAction, execution);
      default:
        return null;
    }
  }

  private async executeAction(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    // Use the enhanced version that supports all action types
    return await this.executeActionEnhanced(action, execution);
  }

  private async sendEmailAction(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    const config = action.config || {};
    const emailTemplateId = config.emailTemplateId;

    if (!emailTemplateId) {
      throw new Error('Email template ID is required');
    }

    const contactData = execution.context?.contactData || {};
    if (!contactData?.email) {
      throw new Error('Contact email is required');
    }

    if (config.conditionalSend?.enabled) {
      const requiredTag = config.conditionalSend.tagId || config.conditionalSend.tagName;
      if (requiredTag) {
        const contactTags = Array.isArray(contactData.tags) ? contactData.tags : Array.isArray(contactData.Tags) ? contactData.Tags : [];
        if (!contactTags.includes(requiredTag)) {
          return { skipped: true, reason: 'conditional_send_not_met' };
        }
      }
    }

    await this.handleSendTiming(config.sendOptions);

    const template = await getEmailTemplate(emailTemplateId);
    if (!template) {
      throw new Error('Email template not found');
    }

    const automation = await this.getAutomation(execution.automationId);
    const automationName = automation?.name || execution.automationId;

    const subjectPrimary = this.processTemplate(config.subject || template.subject, contactData);
    let subjectToSend = subjectPrimary;
    if (config.abTest?.enabled && config.abTest.subjectB) {
      const split = Math.min(Math.max(Number(config.abTest.split ?? 50), 1), 99);
      const roll = Math.random() * 100;
      if (roll >= split) {
        subjectToSend = this.processTemplate(config.abTest.subjectB, contactData);
      }
    }

    const unsubscribeUrl = buildUnsubscribeApiUrl({
      email: contactData.email,
      userId: automation?.userId,
      workspaceId: automation?.workspaceId || contactData.workspaceId,
    });

    let htmlBody = applyUnsubscribeUrlToHtml(
      this.processTemplate(template.htmlContent, contactData),
      unsubscribeUrl
    );
    const textBody = applyUnsubscribeUrlToHtml(
      this.processTemplate(template.textContent || '', contactData),
      unsubscribeUrl
    );

    const trackingCfg = config.tracking || {};
    const openTracking = trackingCfg.open !== false;
    const clickTracking = trackingCfg.click !== false;
    const workspaceId = automation?.workspaceId || contactData.workspaceId;

    if (workspaceId && (openTracking || clickTracking)) {
      htmlBody = await prepareAutomationEmailHtml(htmlBody, {
        workspaceId,
        automationId: execution.automationId,
        actionId: action.id,
        automationName,
        recipientEmail: contactData.email,
        recipientId: execution.contactId,
        designJson: template.designJson,
        htmlContent: htmlBody,
        openTracking,
        clickTracking,
      });
    } else if (config.utm || config.tracking?.utm) {
      htmlBody = this.appendUtmParameters(htmlBody, action.id);
    }

    const fromEmail = (config.fromEmail || 'info@scubamail.io').trim();
    const fromName = (config.fromName || 'Scubamail').trim();
    const replyToEmail = (config.replyTo || config.fromEmail || undefined) as string | undefined;

    const result = await EmailService.sendEmail({
      to: contactData.email,
      subject: subjectToSend,
      htmlBody,
      textBody,
      fromEmail,
      fromName,
      replyToEmail,
      userId: automation?.userId || execution.context?.contactData?.userId || undefined,
      workspaceId: workspaceId || undefined,
      unsubscribeUrl,
      templateId: emailTemplateId,
      tags: {
        AutomationId: execution.automationId,
        AutomationActionId: action.id,
      },
    });

    await this.updateAutomationStats(execution.automationId, 'emailsSent', 1);

    return result;
  }

  private async addTagAction(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    const { tagId, tagName } = action.config;

    if (tagId) {
      await contactService.addTagToContact(execution.contactId, tagId);
    } else if (tagName) {
      // Create tag if it doesn't exist
      const tagId = await tagService.createTag(execution.context.contactData.userId, {
        name: tagName,
        color: '#3B82F6'
      });
      await contactService.addTagToContact(execution.contactId, tagId);
    }

    return { success: true };
  }

  private async removeTagAction(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    const { tagId } = action.config;

    if (tagId) {
      await contactService.removeTagFromContact(execution.contactId, tagId);
    }

    return { success: true };
  }

  private async updateContactAction(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    const { fieldUpdates } = action.config;

    if (fieldUpdates) {
      await contactService.updateContact(execution.contactId, fieldUpdates);
    }

    return { success: true };
  }

  private async delayAction(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    const config: any = action.config;
    console.log('⏰ delayAction called with config:', JSON.stringify(config, null, 2));

    let delayMs = 0;

    // Format 1: New format with amount + unit (from AutomationEditor)
    if (config.amount !== undefined && config.unit) {
      const amount = Number(config.amount);
      const unit = config.unit;
      
      console.log(`⏰ Using amount+unit format: ${amount} ${unit}`);
      
      // Convert to milliseconds
      const multipliers: Record<string, number> = {
        minutes: 60 * 1000,
        hours: 60 * 60 * 1000,
        days: 24 * 60 * 60 * 1000,
        weeks: 7 * 24 * 60 * 60 * 1000
      };
      
      delayMs = amount * (multipliers[unit] || multipliers.minutes);
    }
    // Format 2: Legacy format with waitType + waitValue
    else if (config.waitType === 'fixed' && config.waitValue !== undefined) {
      console.log(`⏰ Using waitType+waitValue format: ${config.waitValue} hours`);
      delayMs = config.waitValue * 60 * 60 * 1000; // Convert hours to milliseconds
    }
    // Format 3: Until specific date
    else if (config.waitType === 'until_date' && config.waitUntilDate) {
      const now = new Date();
      const targetDate = config.waitUntilDate.toDate();
      delayMs = Math.max(0, targetDate.getTime() - now.getTime());
      console.log(`⏰ Using until_date format: waiting until ${targetDate.toISOString()}`);
    }
    else {
      console.warn('⚠️ Unknown delay config format, defaulting to no delay');
    }

    if (delayMs > 0) {
      console.log(`⏰ Waiting for ${delayMs}ms (${Math.round(delayMs / 1000)}s)`);
      await new Promise(resolve => setTimeout(resolve, delayMs));
      console.log('✅ Delay completed');
    }

    return { success: true };
  }

  private async evaluateCondition(condition: AutomationCondition, execution: AutomationExecution): Promise<boolean> {
    const { type, operator, value, config } = condition;
    const contactData = execution.context.contactData;

    let fieldValue: any;

    switch (type) {
      case 'contact_field':
        fieldValue = contactData[config.field || ''];
        break;
      case 'contact_tag':
        fieldValue = contactData.tags?.includes(config.tagId || '');
        break;
      case 'contact_segment':
        fieldValue = contactData.segments?.includes(config.segmentId || '');
        break;
      default:
        return false;
    }

    return this.compareValues(fieldValue, operator, value);
  }

  private compareValues(fieldValue: any, operator: string, expectedValue: any): boolean {
    switch (operator) {
      case 'equals':
        return fieldValue === expectedValue;
      case 'not_equals':
        return fieldValue !== expectedValue;
      case 'contains':
        return String(fieldValue).includes(String(expectedValue));
      case 'not_contains':
        return !String(fieldValue).includes(String(expectedValue));
      case 'greater_than':
        return Number(fieldValue) > Number(expectedValue);
      case 'less_than':
        return Number(fieldValue) < Number(expectedValue);
      case 'is_empty':
        return !fieldValue || fieldValue === '';
      case 'is_not_empty':
        return fieldValue && fieldValue !== '';
      default:
        return false;
    }
  }

  private processTemplate(template: string, contactData: any): string {
    if (!template) return '';

    return template.replace(/\{\{(\w+)\}\}/g, (match, field) => {
      return contactData[field] || match;
    });
  }

  private async handleSendTiming(sendOptions?: {
    mode?: 'immediate' | 'delay' | 'specific_time';
    delayValue?: number;
    delayUnit?: 'minutes' | 'hours' | 'days';
    sendTime?: string;
    timezone?: string;
  }): Promise<void> {
    if (!sendOptions || !sendOptions.mode || sendOptions.mode === 'immediate') {
      return;
    }
    if (sendOptions.mode === 'delay') {
      const value = Number(sendOptions.delayValue || 0);
      if (value <= 0) return;
      const multiplier: Record<string, number> = {
        minutes: 60 * 1000,
        hours: 60 * 60 * 1000,
        days: 24 * 60 * 60 * 1000,
      };
      const unit = sendOptions.delayUnit || 'hours';
      const delayMs = value * (multiplier[unit] || multiplier.hours);
      await this.waitForMs(delayMs);
      return;
    }
    if (sendOptions.mode === 'specific_time') {
      const delayMs = this.computeMsUntilTime(sendOptions.sendTime, sendOptions.timezone);
      if (delayMs > 0) {
        await this.waitForMs(delayMs);
      }
    }
  }

  private async waitForMs(ms: number): Promise<void> {
    if (!Number.isFinite(ms) || ms <= 0) return;
    await new Promise(resolve => setTimeout(resolve, ms));
  }

  private computeMsUntilTime(sendTime?: string, timezone?: string): number {
    if (!sendTime) return 0;
    try {
      const [hours, minutes] = sendTime.split(':').map(Number);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return 0;
      }
      const now = new Date();
      if (!timezone) {
        const target = new Date(now);
        target.setHours(hours, minutes, 0, 0);
        if (target <= now) {
          target.setDate(target.getDate() + 1);
        }
        return Math.max(0, target.getTime() - now.getTime());
      }
      const localeString = now.toLocaleString('en-US', { timeZone: timezone });
      const tzNow = new Date(localeString);
      const tzTarget = new Date(localeString);
      tzTarget.setHours(hours, minutes, 0, 0);
      if (tzTarget <= tzNow) {
        tzTarget.setDate(tzTarget.getDate() + 1);
      }
      const tzTargetUtc = new Date(tzTarget.toLocaleString('en-US', { timeZone: 'UTC' }));
      return Math.max(0, tzTargetUtc.getTime() - now.getTime());
    } catch (error) {
      console.warn('Failed to compute scheduled send time:', error);
      return 0;
    }
  }

  private appendUtmParameters(html: string, actionId: string): string {
    if (!html) return html;
    try {
      const linkRegex = /<a\s+([^>]*?)href=["']([^"']*?)["']([^>]*?)>/gi;
      return html.replace(linkRegex, (match, before, url, after) => {
        if (!url || url.startsWith('mailto:') || url.startsWith('#') || url.includes('utm_')) {
          return match;
        }
        const separator = url.includes('?') ? '&' : '?';
        const params = new URLSearchParams({
          utm_source: 'scubamail',
          utm_medium: 'automation',
          utm_campaign: 'automation_flow',
          utm_content: actionId || 'automation_email',
        });
        return `<a ${before}href="${url}${separator}${params.toString()}"${after}>`;
      });
    } catch (error) {
      console.warn('Failed to append UTM parameters:', error);
      return html;
    }
  }

  private getNextStepId(step: AutomationStep, result: any): string | null {
    // This would implement the logic to determine the next step
    // based on the step type and result
    return step.connections[0] || null;
  }

  private async recordStepExecution(executionId: string, stepId: string, status: 'success' | 'failed' | 'skipped', result?: any, error?: string, automation?: Automation): Promise<void> {
    const execution = await this.getExecution(executionId);
    if (!execution) return;

    const stepHistory = {
      stepId,
      executedAt: new Date(), // Use JavaScript Date instead of serverTimestamp for array fields
      status,
      result,
      error
    };

    let nextStepId: string | null = null;
    if (status === 'success' && automation) {
      const step = automation.steps.find((s: AutomationStep) => s.id === stepId);
      if (step) {
        nextStepId = this.getNextStepId(step, result);
      }
    }

    await this.updateExecution(executionId, {
      stepHistory: [...execution.stepHistory, stepHistory],
      currentStepId: nextStepId || stepId
    });
  }

  private async updateExecution(executionId: string, updates: Partial<AutomationExecution>): Promise<void> {
    const docRef = doc(db, 'automationExecutions', executionId);
    await updateDoc(docRef, updates);
  }

  private async getExecution(executionId: string): Promise<AutomationExecution | null> {
    const docRef = doc(db, 'automationExecutions', executionId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as AutomationExecution;
    }
    return null;
  }

  private async updateAutomationStats(automationId: string, field: string, increment: number): Promise<void> {
    const automation = await this.getAutomation(automationId);
    if (!automation) return;

    const newStats = {
      ...automation.statistics,
      [field]: automation.statistics[field] + increment
    };

    await this.updateAutomation(automationId, { statistics: newStats });
  }

  // Template Management
  async getAutomationTemplates(): Promise<AutomationTemplate[]> {
    try {
      // Import the local templates
      const { automationTemplates } = await import('./automationTemplates');
      return automationTemplates;
    } catch (error) {
      console.error('Error getting automation templates:', error);
      throw new Error('Failed to get automation templates');
    }
  }

  async createAutomationFromTemplate(templateId: string, userId: string, workspaceId?: string): Promise<string> {
    try {
      console.log('Creating automation from template:', { templateId, userId, workspaceId });
      
      // Import the local templates
      const { automationTemplates } = await import('./automationTemplates');

      const template = automationTemplates.find(t => t.id === templateId);
      if (!template) {
        console.error('Template not found:', templateId);
        throw new Error('Template not found');
      }

      console.log('Found template:', template.name);

      const automation: Omit<Automation, 'id' | 'createdAt' | 'updatedAt' | 'statistics' | 'version'> = {
        name: template.name,
        description: template.description,
        status: 'draft',
        userId,
        ...(workspaceId ? { workspaceId } : {}),
        steps: template.steps.map((step, index) => ({
          ...step,
          id: `step_${Date.now()}_${index}`,
          position: { x: 100, y: 50 + (index * 150) }, // Position vertically
          // Fix the config structure - the template has nested config, but we need it flat
          config: step.config as AutomationTrigger | AutomationCondition | AutomationAction
        })),
        settings: {
          allowRepetition: false,
          openTracking: true,
          clickTracking: true,
          personalizedToField: true,
          googleAnalyticsTracking: false
        }
      };

      console.log('Created automation object, calling createAutomation...');
      const automationId = await this.createAutomation(automation);
      console.log('Automation created successfully:', automationId);
      return automationId;
    } catch (error: any) {
      console.error('Error creating automation from template:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw new Error(`Failed to create automation from template: ${error.message || 'Unknown error'}`);
    }
  }

  async createAutomationFromTemplateData(
    template: { name: string; description?: string; steps: Array<Partial<AutomationStep>> },
    userId: string,
    workspaceId?: string
  ): Promise<string> {
    try {
      if (!template?.steps || template.steps.length === 0) {
        throw new Error('Template has no steps');
      }

      const timestampBase = Date.now();
      const normalizedSteps: AutomationStep[] = template.steps.map((step, index) => {
        const generatedId = step.id || `step_${timestampBase}_${index}`;
        const existingPosition = step.position || { x: 100, y: 50 + (index * 150) };

        // Sanitize config based on step type to ensure all required fields exist
        let sanitizedConfig: any = step.config || {};
        
        // Templates use simplified type names - need to check both step.type and config values
        const templateType = (step as any).type; // Template might use 'email', 'tag', etc.
        
        if (templateType === 'email' || sanitizedConfig.emailTemplateId !== undefined || sanitizedConfig.subject !== undefined) {
          // Ensure email configs have all required fields (no undefined values)
          sanitizedConfig = {
            name: sanitizedConfig.name || 'Email',
            subject: sanitizedConfig.subject || '',
            preheader: sanitizedConfig.preheader || '',
            emailTemplateId: sanitizedConfig.emailTemplateId || '',
            fromEmail: sanitizedConfig.fromEmail || '',
            sendMode: sanitizedConfig.sendMode || 'immediately',
            sendTime: sanitizedConfig.sendTime || null,
            trackOpens: sanitizedConfig.trackOpens !== false,
            trackClicks: sanitizedConfig.trackClicks !== false
          };
        } else if (templateType === 'tag' || sanitizedConfig.tag !== undefined) {
          sanitizedConfig = {
            action: sanitizedConfig.action || 'add',
            tag: sanitizedConfig.tag || ''
          };
        } else if (step.type === 'delay' || sanitizedConfig.amount !== undefined) {
          sanitizedConfig = {
            amount: sanitizedConfig.amount || 1,
            unit: sanitizedConfig.unit || 'hours'
          };
        } else if (step.type === 'trigger') {
          sanitizedConfig = {
            event: sanitizedConfig.event || 'contact_added',
            title: sanitizedConfig.title || 'Trigger',
            description: sanitizedConfig.description || '',
            triggerFilters: Array.isArray(sanitizedConfig.triggerFilters) ? sanitizedConfig.triggerFilters : [],
            ...(sanitizedConfig.tag ? { tag: sanitizedConfig.tag } : {}),
            ...(sanitizedConfig.contactFilter ? { contactFilter: sanitizedConfig.contactFilter } : {})
          };
        } else if (step.type === 'split') {
          sanitizedConfig = {
            title: sanitizedConfig.title || 'Condition',
            description: sanitizedConfig.description || '',
            conditionType: sanitizedConfig.conditionType || 'email_opened',
            ...(sanitizedConfig.conditions ? { conditions: sanitizedConfig.conditions } : {})
          };
        }

        return {
          id: generatedId,
          type: (step.type as AutomationStep['type']) || 'action',
          title: step.title || `Step ${index + 1}`,
          description: step.description || '',
          config: sanitizedConfig,
          position: existingPosition,
          connections: Array.isArray(step.connections) ? step.connections : [],
          enabled: step.enabled !== false
        };
      });

      const automation: Omit<Automation, 'id' | 'createdAt' | 'updatedAt' | 'statistics' | 'version'> = {
        name: template.name,
        description: template.description || 'Automated workflow',
        status: 'draft',
        userId,
        ...(workspaceId ? { workspaceId } : {}),
        steps: normalizedSteps,
        settings: {
          allowRepetition: false,
          openTracking: true,
          clickTracking: true,
          personalizedToField: true,
          googleAnalyticsTracking: false
        }
      };

      return await this.createAutomation(automation);
    } catch (error: any) {
      console.error('Error creating automation from template data:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw new Error(`Failed to create automation from template data: ${error.message || 'Unknown error'}`);
    }
  }

  // Real-time listeners
  subscribeToAutomations(userId: string, callback: (automations: Automation[]) => void): () => void {
    const q = query(
      collection(db, 'automations'),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );

    return onSnapshot(q, (querySnapshot) => {
      const automations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Automation[];
      callback(automations);
    });
  }

  subscribeToAutomationExecutions(automationId: string, callback: (executions: AutomationExecution[]) => void): () => void {
    console.log(`Subscribing to executions for automation: ${automationId}`);
    
    // Try with orderBy first
    const qWithOrder = query(
      collection(db, 'automationExecutions'),
      where('automationId', '==', automationId),
      orderBy('startedAt', 'desc')
    );

    let unsubscribe = onSnapshot(
      qWithOrder,
      (querySnapshot) => {
        console.log(`Subscription update: ${querySnapshot.docs.length} executions`);
        const executions = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as AutomationExecution[];
        callback(executions);
      },
      (error: any) => {
        console.error('Error in execution subscription:', error);
        // If index error, fall back to query without orderBy
        if (error.code === 'failed-precondition' || error.message?.includes('index')) {
          console.warn('Index not available, subscribing without orderBy');
          const qWithoutOrder = query(
            collection(db, 'automationExecutions'),
            where('automationId', '==', automationId)
          );
          
          unsubscribe = onSnapshot(qWithoutOrder, (querySnapshot) => {
            console.log(`Subscription update (no ordering): ${querySnapshot.docs.length} executions`);
            const executions = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })) as AutomationExecution[];
            
            // Sort in memory
            executions.sort((a, b) => {
              const aTime = (a.startedAt as any)?.toMillis?.() || 0;
              const bTime = (b.startedAt as any)?.toMillis?.() || 0;
              return bTime - aTime;
            });
            
            callback(executions);
          });
        }
      }
    );

    return () => unsubscribe();
  }

  // Fetch recent executions once (e.g., for analytics initial load)
  async getAutomationExecutions(automationId: string, limitCount: number = 100): Promise<AutomationExecution[]> {
    try {
      console.log(`Fetching executions for automation: ${automationId}`);
      const qExec = query(
        collection(db, 'automationExecutions'),
        where('automationId', '==', automationId),
        orderBy('startedAt', 'desc')
      );
      const snap = await getDocs(qExec);
      console.log(`Found ${snap.docs.length} executions for automation ${automationId}`);
      const items = snap.docs.slice(0, limitCount).map(doc => ({ id: doc.id, ...doc.data() })) as AutomationExecution[];
      return items;
    } catch (error: any) {
      console.error('Error fetching automation executions:', error);
      // If index doesn't exist, try without orderBy
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        console.warn('Firestore index not available, fetching without orderBy');
        try {
          const qExec = query(
            collection(db, 'automationExecutions'),
            where('automationId', '==', automationId)
          );
          const snap = await getDocs(qExec);
          console.log(`Found ${snap.docs.length} executions (without ordering)`);
          const items = snap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as AutomationExecution[];
          // Sort in memory
          items.sort((a, b) => {
            const aTime = (a.startedAt as any)?.toMillis?.() || 0;
            const bTime = (b.startedAt as any)?.toMillis?.() || 0;
            return bTime - aTime;
          });
          return items.slice(0, limitCount);
        } catch (fallbackError) {
          console.error('Error in fallback query:', fallbackError);
          return [];
        }
      }
      return [];
    }
  }

  // Goal Tracking Methods
  async addGoalToAutomation(automationId: string, goal: Omit<AutomationGoal, 'id' | 'currentValue' | 'completionRate'>): Promise<string> {
    try {
      const automation = await this.getAutomation(automationId);
      if (!automation) throw new Error('Automation not found');

      const newGoal: AutomationGoal = {
        ...goal,
        id: `goal_${Date.now()}`,
        currentValue: 0,
        completionRate: 0
      };

      const updatedGoals = [...(automation.goals || []), newGoal];

      await this.updateAutomation(automationId, { goals: updatedGoals });
      return newGoal.id;
    } catch (error) {
      console.error('Error adding goal:', error);
      throw new Error('Failed to add goal');
    }
  }

  async updateGoalProgress(automationId: string, goalId: string, value: number): Promise<void> {
    try {
      const automation = await this.getAutomation(automationId);
      if (!automation) throw new Error('Automation not found');

      const goals = automation.goals || [];
      const goalIndex = goals.findIndex(g => g.id === goalId);

      if (goalIndex === -1) throw new Error('Goal not found');

      const goal = goals[goalIndex];
      goal.currentValue += value;

      if (goal.targetValue) {
        goal.completionRate = Math.min(100, (goal.currentValue / goal.targetValue) * 100);
      }

      goals[goalIndex] = goal;
      await this.updateAutomation(automationId, { goals });

      // Update statistics
      const goalCompletions = automation.statistics.goalCompletions || {};
      goalCompletions[goalId] = (goalCompletions[goalId] || 0) + value;

      await this.updateAutomation(automationId, {
        statistics: {
          ...automation.statistics,
          goalCompletions
        }
      });
    } catch (error) {
      console.error('Error updating goal progress:', error);
      throw error;
    }
  }

  async trackConversion(automationId: string, executionId: string, value: number = 1, metadata?: any): Promise<void> {
    try {
      const automation = await this.getAutomation(automationId);
      if (!automation) return;

      const newStats = {
        ...automation.statistics,
        conversions: (automation.statistics.conversions || 0) + 1,
        revenue: (automation.statistics.revenue || 0) + value
      };

      await this.updateAutomation(automationId, { statistics: newStats });

      // Record conversion event
      await addDoc(collection(db, 'automationConversions'), {
        automationId,
        executionId,
        value,
        metadata,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      console.error('Error tracking conversion:', error);
    }
  }

  // Lead Scoring Methods
  async updateLeadScore(contactId: string, scoreChange: number, reason: string): Promise<void> {
    try {
      const contact = await contactService.getContact(contactId);
      if (!contact) throw new Error('Contact not found');

      const currentScore = (contact as any).leadScore || 0;
      const newScore = Math.max(0, currentScore + scoreChange);

      await contactService.updateContact(contactId, {
        leadScore: newScore,
        leadScoreHistory: [
          ...((contact as any).leadScoreHistory || []),
          {
            change: scoreChange,
            reason,
            timestamp: new Date(),
            previousScore: currentScore,
            newScore
          }
        ],
        lastLeadScoreUpdate: new Date()
      });

      // Auto-segment based on score
      if (newScore >= 80) {
        // Hot lead
        await this.addTagAction({
          id: 'auto',
          type: 'add_tag',
          config: { tagName: 'Hot Lead' }
        }, { contactId } as any);
      } else if (newScore >= 50) {
        // Warm lead
        await this.addTagAction({
          id: 'auto',
          type: 'add_tag',
          config: { tagName: 'Warm Lead' }
        }, { contactId } as any);
      }
    } catch (error) {
      console.error('Error updating lead score:', error);
      throw error;
    }
  }

  // SMS Action Handler
  private async sendSMSAction(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    const { smsMessage, smsFrom } = action.config;

    if (!smsMessage) {
      throw new Error('SMS message is required');
    }

    const processedMessage = this.processTemplate(smsMessage, execution.context.contactData);
    const phoneNumber = execution.context.contactData.phone || execution.context.contactData.phoneNumber;

    if (!phoneNumber) {
      throw new Error('Contact does not have a phone number');
    }

    // TODO: Integrate with SMS provider (Twilio, AWS SNS, etc.)
    console.log('Sending SMS:', {
      to: phoneNumber,
      from: smsFrom || 'Scubamail',
      message: processedMessage
    });

    // Simulate SMS sending
    const result = {
      success: true,
      messageId: `sms_${Date.now()}`,
      to: phoneNumber,
      status: 'sent'
    };

    // Update automation statistics
    await this.updateAutomationStats(execution.automationId, 'smsSent', 1);

    return result;
  }

  // Push Notification Action Handler
  private async sendPushAction(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    const { pushTitle, pushMessage, pushIcon, pushUrl } = action.config;

    if (!pushTitle || !pushMessage) {
      throw new Error('Push title and message are required');
    }

    const processedTitle = this.processTemplate(pushTitle, execution.context.contactData);
    const processedMessage = this.processTemplate(pushMessage, execution.context.contactData);
    const processedUrl = pushUrl ? this.processTemplate(pushUrl, execution.context.contactData) : undefined;

    // TODO: Integrate with push notification service (Firebase, OneSignal, etc.)
    console.log('Sending push notification:', {
      title: processedTitle,
      message: processedMessage,
      icon: pushIcon,
      url: processedUrl,
      userId: execution.context.contactData.userId || execution.contactId
    });

    // Simulate push sending
    const result = {
      success: true,
      notificationId: `push_${Date.now()}`,
      status: 'sent'
    };

    // Update automation statistics
    await this.updateAutomationStats(execution.automationId, 'pushSent', 1);

    return result;
  }

  // A/B Test Action Handler
  private async abTestAction(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    const { abVariants, abGoal, abWinnerDelay, abAutoSelectWinner } = action.config;

    if (!abVariants || abVariants.length < 2) {
      throw new Error('At least 2 variants are required for A/B test');
    }

    // Select variant based on percentage
    const random = Math.random() * 100;
    let cumulative = 0;
    let selectedVariant = abVariants[0];

    for (const variant of abVariants) {
      cumulative += variant.percentage;
      if (random <= cumulative) {
        selectedVariant = variant;
        break;
      }
    }

    // Record A/B test assignment
    await addDoc(collection(db, 'automationABTests'), {
      automationId: execution.automationId,
      executionId: execution.id,
      contactId: execution.contactId,
      variantName: selectedVariant.name,
      goal: abGoal,
      assignedAt: serverTimestamp()
    });

    // Execute the selected variant
    if (selectedVariant.emailTemplateId) {
      const emailAction: AutomationAction = {
        id: 'ab_email',
        type: 'send_email',
        config: {
          emailTemplateId: selectedVariant.emailTemplateId,
          subject: selectedVariant.subject
        }
      };
      await this.sendEmailAction(emailAction, execution);
    }

    return {
      success: true,
      selectedVariant: selectedVariant.name,
      nextStepId: selectedVariant.nextStepId
    };
  }

  // Goal Tracking Action
  private async trackGoalAction(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    const { goalName, goalValue, goalMetadata } = action.config;

    if (!goalName) {
      throw new Error('Goal name is required');
    }

    await addDoc(collection(db, 'automationGoalEvents'), {
      automationId: execution.automationId,
      executionId: execution.id,
      contactId: execution.contactId,
      goalName,
      value: goalValue || 1,
      metadata: goalMetadata,
      timestamp: serverTimestamp()
    });

    // Find and update the corresponding goal
    const automation = await this.getAutomation(execution.automationId);
    if (automation?.goals) {
      const goal = automation.goals.find(g => g.name === goalName);
      if (goal) {
        await this.updateGoalProgress(execution.automationId, goal.id, goalValue || 1);
      }
    }

    return { success: true, goalName, value: goalValue || 1 };
  }

  // Conversion Tracking Action
  private async trackConversionAction(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    const { conversionName, conversionValue, conversionMetadata } = action.config;

    await this.trackConversion(
      execution.automationId,
      execution.id,
      conversionValue || 1,
      { name: conversionName, ...conversionMetadata }
    );

    return { success: true, conversionName, value: conversionValue || 1 };
  }

  // Lead Score Update Action
  private async updateLeadScoreAction(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    const { scoreChange, scoreReason } = action.config;

    if (scoreChange === undefined) {
      throw new Error('Score change is required');
    }

    await this.updateLeadScore(execution.contactId, scoreChange, scoreReason || 'Automation action');

    return { success: true, scoreChange, reason: scoreReason };
  }

  // Automation Versioning
  async createAutomationVersion(automationId: string): Promise<string> {
    try {
      const automation = await this.getAutomation(automationId);
      if (!automation) throw new Error('Automation not found');

      // Save current version as a snapshot
      const versionData = {
        ...automation,
        originalId: automationId,
        versionNumber: automation.version,
        createdAt: serverTimestamp()
      };

      const versionRef = await addDoc(collection(db, 'automationVersions'), versionData);

      // Update automation with new version number and reference
      await this.updateAutomation(automationId, {
        version: automation.version + 1,
        previousVersions: [...(automation.previousVersions || []), versionRef.id]
      });

      return versionRef.id;
    } catch (error) {
      console.error('Error creating automation version:', error);
      throw new Error('Failed to create automation version');
    }
  }

  async rollbackToVersion(automationId: string, versionId: string): Promise<void> {
    try {
      const versionDoc = await getDoc(doc(db, 'automationVersions', versionId));
      if (!versionDoc.exists()) throw new Error('Version not found');

      const versionData = versionDoc.data();

      // Create a new version of current state before rollback
      await this.createAutomationVersion(automationId);

      // Restore the version data
      const { originalId, versionNumber, createdAt, ...restoreData } = versionData;
      await this.updateAutomation(automationId, {
        ...restoreData,
        version: (versionData.versionNumber as number) + 1
      });
    } catch (error) {
      console.error('Error rolling back automation:', error);
      throw new Error('Failed to rollback automation');
    }
  }

  // Enhanced delay action with business days and timezone support
  private async delayActionEnhanced(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    const config: any = action.config;
    console.log('⏰ delayActionEnhanced called with config:', JSON.stringify(config, null, 2));

    let delayMs = 0;

    // Format 1: New format with amount + unit (from AutomationEditor)
    if (config.amount !== undefined && config.unit) {
      const amount = Number(config.amount);
      const unit = config.unit;
      
      console.log(`⏰ Using amount+unit format: ${amount} ${unit}`);
      
      // Convert to milliseconds
      const multipliers: Record<string, number> = {
        minutes: 60 * 1000,
        hours: 60 * 60 * 1000,
        days: 24 * 60 * 60 * 1000,
        weeks: 7 * 24 * 60 * 60 * 1000
      };
      
      delayMs = amount * (multipliers[unit] || multipliers.minutes);
    }
    // Format 2: Legacy waitType formats
    else if (config.waitType === 'fixed' && config.waitValue !== undefined) {
      console.log(`⏰ Using waitType+waitValue format: ${config.waitValue} hours`);
      delayMs = config.waitValue * 60 * 60 * 1000; // Convert hours to milliseconds
    } else if (config.waitType === 'business_days' && config.waitValue !== undefined) {
      console.log(`⏰ Using business_days format: ${config.waitValue} days`);
      // Calculate business days
      delayMs = this.calculateBusinessDaysDelay(config.waitValue);
    } else if (config.waitType === 'until_date' && config.waitUntilDate) {
      const now = new Date();
      const targetDate = config.waitUntilDate.toDate();
      delayMs = Math.max(0, targetDate.getTime() - now.getTime());
      console.log(`⏰ Using until_date format: waiting until ${targetDate.toISOString()}`);
    } else if (config.waitType === 'until_time_of_day' && config.waitUntilTime) {
      console.log(`⏰ Using until_time_of_day format: ${config.waitUntilTime}`);
      delayMs = this.calculateTimeOfDayDelay(config.waitUntilTime, config.timezone || 'UTC');
    }
    else {
      console.warn('⚠️ Unknown delay config format, defaulting to no delay');
    }

    // Delays are no longer executed client-side.
    //
    // This method previously blocked on `setTimeout` for delays under an hour and
    // wrote longer ones to a `scheduledExecutions` collection that had no reader
    // anywhere in the codebase — so any delay over an hour silently abandoned the
    // execution. Both behaviours are gone; the backend runner
    // (`runBackendAutomationExecution`) checkpoints the delay as
    // `status: 'waiting'` with a `resumeAt` timestamp and
    // `resumeWaitingAutomationExecutions` resumes it, which survives refreshes,
    // deploys and crashes.
    if (delayMs > 0) {
      console.log(
        `⏰ Delay of ${delayMs}ms is handled server-side; not blocking the client.`
      );
    }

    return { success: true, delayMs, deferredToBackend: delayMs > 0 };
  }

  private calculateBusinessDaysDelay(days: number): number {
    let count = 0;
    let current = new Date();

    while (count < days) {
      current.setDate(current.getDate() + 1);
      const dayOfWeek = current.getDay();
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        count++;
      }
    }

    return current.getTime() - Date.now();
  }

  private calculateTimeOfDayDelay(targetTime: string, timezone: string): number {
    const now = new Date();
    const [hours, minutes] = targetTime.split(':').map(Number);

    const target = new Date(now);
    target.setHours(hours, minutes, 0, 0);

    // If target time has passed today, schedule for tomorrow
    if (target.getTime() <= now.getTime()) {
      target.setDate(target.getDate() + 1);
    }

    return target.getTime() - now.getTime();
  }

  // scheduleExecutionContinuation was removed.
  //
  // It wrote {executionId, continueAt, createdAt} documents to a
  // `scheduledExecutions` collection. Nothing ever queried that collection — the
  // only other reference in the repo is a cleanup list in
  // functions/adminDeleteUser.js — so every execution routed through it was
  // silently dropped. Delay resumption is now handled by
  // `resumeWaitingAutomationExecutions` against `automationExecutions`.

  // Enhanced webhook action with retries and timeout
  private async webhookActionEnhanced(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    const { webhookUrl, webhookMethod, webhookHeaders, webhookBody, webhookRetries, webhookTimeout } = action.config;

    if (!webhookUrl) {
      throw new Error('Webhook URL is required');
    }

    const processedBody = this.processTemplate(JSON.stringify(webhookBody || {}), execution.context.contactData);
    const maxRetries = webhookRetries || 3;
    const timeout = webhookTimeout || 30000; // 30 seconds default

    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(webhookUrl, {
          method: webhookMethod || 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...webhookHeaders
          },
          body: processedBody,
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Webhook request failed: ${response.status} ${response.statusText}`);
        }

        const responseData = await response.json().catch(() => ({}));

        return {
          success: true,
          status: response.status,
          data: responseData,
          attempt: attempt + 1
        };
      } catch (error) {
        lastError = error;
        console.error(`Webhook attempt ${attempt + 1} failed:`, error);

        if (attempt < maxRetries) {
          // Exponential backoff
          const backoffMs = Math.min(1000 * Math.pow(2, attempt), 10000);
          await new Promise(resolve => setTimeout(resolve, backoffMs));
        }
      }
    }

    throw new Error(`Webhook failed after ${maxRetries + 1} attempts: ${lastError}`);
  }

  // Update executeAction to handle new action types
  private async executeActionEnhanced(action: AutomationAction, execution: AutomationExecution): Promise<any> {
    switch (action.type) {
      case 'send_email':
        return await this.sendEmailAction(action, execution);
      case 'send_sms':
        return await this.sendSMSAction(action, execution);
      case 'send_push':
        return await this.sendPushAction(action, execution);
      case 'add_tag':
        return await this.addTagAction(action, execution);
      case 'remove_tag':
        return await this.removeTagAction(action, execution);
      case 'update_contact':
        return await this.updateContactAction(action, execution);
      case 'update_lead_score':
        return await this.updateLeadScoreAction(action, execution);
      case 'ab_test':
        return await this.abTestAction(action, execution);
      case 'track_goal':
        return await this.trackGoalAction(action, execution);
      case 'track_conversion':
        return await this.trackConversionAction(action, execution);
      case 'wait':
      case 'delay':
        return await this.delayActionEnhanced(action, execution);
      case 'webhook':
        return await this.webhookActionEnhanced(action, execution);
      default:
        return null;
    }
  }
}

export const automationService = new AutomationService();
